import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { 
  LayoutDashboard, 
  Truck, 
  Users, 
  Building2, 
  LogOut, 
  Menu, 
  X,
  ChevronDown,
  Bell,
  FileText,
  CalendarDays,
  CalendarRange,
  FileCheck,
  ShoppingCart,
  Receipt,
  CreditCard,
  ShieldAlert,
  KeyRound,
  Settings,
  Wallet,
  BookOpen,
  User,
  MapPin,
  Smartphone,
  Navigation,
  Code
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardLayout = () => {
  const { user, logout, activeCompanyId, companies, switchCompany, getActiveCompany } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const activeCompany = getActiveCompany();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setChangingPassword(true);
    
    try {
      await axios.post(`${API_URL}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success("Password changed successfully!");
      setChangePasswordOpen(false);
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      staff: "bg-blue-500/20 text-blue-400 border-blue-500/50",
      admin: "bg-green-500/20 text-green-400 border-green-500/50",
      super_admin: "bg-purple-500/20 text-purple-400 border-purple-500/50"
    };
    const labels = {
      staff: "Staff",
      admin: "Admin",
      super_admin: "Super Admin"
    };
    return { style: styles[role] || styles.staff, label: labels[role] || role };
  };

  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/dashboard/orders", icon: ShoppingCart, label: "Orders" },
    { to: "/dashboard/load-slips", icon: FileText, label: "Load Slips" },
    { to: "/dashboard/online-builty", icon: BookOpen, label: "Online Builty" },
    { to: "/dashboard/invoices", icon: Receipt, label: "Invoices" },
    { to: "/dashboard/accounting", icon: Wallet, label: "Accounting" },
    { to: "/dashboard/trucks", icon: Truck, label: "Truck Master" },
    { to: "/dashboard/drivers", icon: User, label: "Driver Master" },
    { to: "/dashboard/gps-tracking", icon: MapPin, label: "GPS Tracking" },
    { to: "/dashboard/sim-fastag-tracking", icon: Smartphone, label: "SIM & FASTag" },
    { to: "/dashboard/vendors", icon: Truck, label: "Vendors" },
    { to: "/dashboard/clients", icon: Building2, label: "Clients" },
    { to: "/dashboard/employees", icon: Users, label: "Employees" },
    { to: "/dashboard/attendance", icon: CalendarDays, label: "Attendance" },
    { to: "/dashboard/leave-balance", icon: CalendarRange, label: "Leave Balance" },
    { to: "/dashboard/offer-letters", icon: FileCheck, label: "Offer Letters" },
    { to: "/dashboard/pan-verification", icon: CreditCard, label: "PAN Verification" },
    { to: "/dashboard/vehicle-rc-verification", icon: Truck, label: "Vehicle RC Verify" },
    ...((user?.role === "admin" || user?.role === "super_admin") ? [
      { to: "/dashboard/user-management", icon: ShieldAlert, label: "User Management" },
      { to: "/dashboard/company-settings", icon: Settings, label: "Company Settings" },
      { to: "/dashboard/api-platform", icon: Code, label: "API Platform" }
    ] : []),
    // Super Admin only
    ...(user?.role === "super_admin" ? [
      { to: "/dashboard/companies", icon: Building2, label: "Companies" }
    ] : []),
  ];

  const NavItem = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all text-sm ${
          isActive
            ? "bg-primary/20 text-primary border-l-2 border-primary"
            : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
        }`
      }
      onClick={() => setSidebarOpen(false)}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className="w-4 h-4" />
      <span className="font-medium">{label}</span>
    </NavLink>
  );

  const roleInfo = getRoleBadge(user?.role);

  return (
    <div className="min-h-screen bg-background flex" data-testid="dashboard-layout">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 glass border-r border-border transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-border/50">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_ardhana-log/artifacts/ucgxtluv_WhatsApp%20Image%202026-01-17%20at%2023.49.17.jpeg" 
                alt="Logo" 
                className="w-10 h-10 rounded-lg"
              />
              <div>
                <h1 className="font-heading text-lg font-bold text-foreground">ARDHANAGESHWAR</h1>
                <p className="text-xs text-muted-foreground">Logistics TMS</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-border/50">
            <div className="flex items-center gap-3 px-2 py-2">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-primary font-semibold text-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                <Badge variant="outline" className={`text-xs ${roleInfo.style}`}>
                  {roleInfo.label}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Letterhead Bar */}
        <div className="bg-gradient-to-r from-primary/10 via-card to-primary/10 border-b border-border/50 px-4 lg:px-6 py-3 hidden lg:block">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_ardhana-log/artifacts/ucgxtluv_WhatsApp%20Image%202026-01-17%20at%2023.49.17.jpeg" 
                alt="Ardhanageshwar Logistics" 
                className="w-12 h-12 rounded-lg border-2 border-primary/30"
              />
              <div>
                <h1 className="font-heading text-xl font-bold text-primary tracking-wide">
                  ARDHANAGESHWAR LOGISTICS
                </h1>
                <p className="text-muted-foreground text-xs">Pvt. Ltd. | Transport & Logistics Solutions</p>
              </div>
            </div>
            <div className="text-right text-xs text-muted-foreground">
              <p className="font-medium text-foreground">Transport Management System</p>
              <p>Mobile: <a href="tel:+919395170701" className="hover:text-primary">+91 93951 70701</a></p>
              <p>Email: <a href="mailto:info.arrdhnageshwarlog@gmail.com" className="hover:text-primary">info.arrdhnageshwarlog@gmail.com</a></p>
            </div>
          </div>
        </div>

        {/* Top Bar */}
        <header className="h-16 border-b border-border bg-card/50 backdrop-blur-sm flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <h2 className="font-heading text-xl font-semibold text-foreground hidden sm:block">
              Transport Management System
            </h2>
          </div>

          <div className="flex items-center gap-4">
            {/* Company Switcher for Super Admin */}
            {user?.role === "super_admin" && companies.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2" data-testid="company-switcher">
                    <Building2 className="w-4 h-4 text-primary" />
                    <span className="hidden sm:inline max-w-[150px] truncate">
                      {activeCompany?.name || "Select Company"}
                    </span>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Switch Company</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {companies.map((company) => (
                    <DropdownMenuItem
                      key={company.id}
                      onClick={() => switchCompany(company.id)}
                      className={activeCompanyId === company.id ? "bg-primary/10" : ""}
                      data-testid={`company-option-${company.code}`}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <Building2 className="w-4 h-4" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{company.name}</p>
                          <p className="text-xs text-muted-foreground">{company.code}</p>
                        </div>
                        {activeCompanyId === company.id && (
                          <Badge variant="default" className="text-xs">Active</Badge>
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-accent rounded-full" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-primary font-semibold text-sm">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <span className="text-foreground text-sm">{user?.name}</span>
                    <Badge variant="outline" className={`ml-2 text-xs ${roleInfo.style}`}>
                      {roleInfo.label}
                    </Badge>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div>
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                    <Badge variant="outline" className={`mt-1 text-xs ${roleInfo.style}`}>
                      {roleInfo.label}
                    </Badge>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setChangePasswordOpen(true)} data-testid="change-password-btn">
                  <KeyRound className="w-4 h-4 mr-2" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-primary" />
              Change Password
            </DialogTitle>
            <DialogDescription>
              Update your account password
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="Enter current password"
                required
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="Enter new password (min 6 characters)"
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="Confirm new password"
                required
                data-testid="confirm-new-password-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setChangePasswordOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="glow-primary bg-primary" 
                disabled={changingPassword}
                data-testid="submit-change-password"
              >
                {changingPassword ? "Changing..." : "Change Password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardLayout;
